import type { RigId } from '../AnimationLibrary';

export interface CharacterDefinition {
  id: string;
  name: string;
  modelPath: string;
  rigId: RigId;
  gender: 'male' | 'female';
  animationFolder?: string;
  legacyModelPaths?: string[];
  tags?: string[];
}

/**
 * Central registry that maps character meshes to their rig/animation set.
 * Add new entries here when you introduce a new mesh.
 */
const definitions: CharacterDefinition[] = [
  // Missionaries
  {
    id: 'elder-missionary',
    name: 'Elder Missionary',
    modelPath: '/assets/models/characters/elder_missionary/mesh/Meshy_AI_Character_output.glb',
    rigId: 'elder_missionary',
    gender: 'male',
    animationFolder: '/assets/models/characters/elder_missionary/animations',
    legacyModelPaths: ['/assets/models/characters/missionary/Meshy_AI_Character_output.glb'],
    tags: ['player', 'missionary']
  },
  {
    id: 'sister-missionary',
    name: 'Sister Missionary',
    modelPath: '/assets/models/characters/sister_missionary/mesh/Meshy_AI_Character_output.glb',
    rigId: 'sister_missionary',
    gender: 'female',
    animationFolder: '/assets/models/characters/sister_missionary/animations',
    legacyModelPaths: ['/assets/models/characters/missionary_sister/Meshy_AI_Character_output.glb'],
    tags: ['player', 'missionary']
  },
  // Companions
  {
    id: 'companion-male',
    name: 'Companion (Male)',
    modelPath: '/assets/models/characters/elder_companion/mesh/Meshy_AI_Character_output.glb',
    rigId: 'elder_companion',
    gender: 'male',
    animationFolder: '/assets/models/characters/elder_companion/animations',
    legacyModelPaths: ['/assets/models/characters/companion/Meshy_AI_Character_output.glb'],
    tags: ['companion']
  },
  {
    id: 'companion-female',
    name: 'Companion (Female)',
    modelPath: '/assets/models/characters/sister_companion/mesh/Meshy_AI_Character_output.glb',
    rigId: 'sister_companion',
    gender: 'female',
    animationFolder: '/assets/models/characters/sister_companion/animations',
    legacyModelPaths: [],
    tags: ['companion']
  },
  // Male sets
  { id: 'man_1', name: 'Man 1', modelPath: '/assets/models/characters/man_1/mesh/Meshy_AI_Character_output.glb', rigId: 'man_1', gender: 'male', animationFolder: '/assets/models/characters/man_1/animations', legacyModelPaths: ['/assets/models/characters/man_1/Meshy_AI_Character_output.glb'] },
  { id: 'man_2', name: 'Man 2', modelPath: '/assets/models/characters/man_2/mesh/Meshy_AI_Character_output.glb', rigId: 'man_2', gender: 'male', animationFolder: '/assets/models/characters/man_2/animations', legacyModelPaths: ['/assets/models/characters/man_2/Meshy_AI_Character_output.glb'] },
  { id: 'man_3', name: 'Man 3', modelPath: '/assets/models/characters/man_3/mesh/Meshy_AI_Character_output.glb', rigId: 'man_3', gender: 'male', animationFolder: '/assets/models/characters/man_3/animations', legacyModelPaths: ['/assets/models/characters/man_3/Meshy_AI_Character_output.glb'] },
  // Female sets
  { id: 'woman_1', name: 'Woman 1', modelPath: '/assets/models/characters/woman_1/mesh/Meshy_AI_Character_output.glb', rigId: 'woman_1', gender: 'female', animationFolder: '/assets/models/characters/woman_1/animations', legacyModelPaths: ['/assets/models/characters/woman_1/Meshy_AI_Character_output.glb'] },
  { id: 'woman_2', name: 'Woman 2', modelPath: '/assets/models/characters/woman_2/mesh/Meshy_AI_Character_output.glb', rigId: 'woman_2', gender: 'female', animationFolder: '/assets/models/characters/woman_2/animations', legacyModelPaths: ['/assets/models/characters/woman_2/Meshy_AI_Character_output.glb'] },
  { id: 'woman_3', name: 'Woman 3', modelPath: '/assets/models/characters/woman_3/mesh/Meshy_AI_Character_output.glb', rigId: 'woman_3', gender: 'female', animationFolder: '/assets/models/characters/woman_3/animations', legacyModelPaths: ['/assets/models/characters/woman_3/Meshy_AI_Character_output.glb'] }
];

export class CharacterRegistry {
  public static getAll(): CharacterDefinition[] {
    return definitions;
  }

  public static getById(id: string): CharacterDefinition | undefined {
    return definitions.find((d) => d.id === id);
  }

  public static getRigIdForModel(modelPath?: string): RigId | undefined {
    if (!modelPath) return undefined;
    const entry = this.getByModelPath(modelPath);
    return entry?.rigId;
  }

  public static getByModelPath(modelPath?: string): CharacterDefinition | undefined {
    if (!modelPath) return undefined;
    return definitions.find((d) =>
      d.modelPath === modelPath || d.legacyModelPaths?.includes(modelPath)
    );
  }

  public static getAnimationFolderForRig(rigId: RigId): string | undefined {
    const entry = definitions.find((d) => d.rigId === rigId);
    return entry?.animationFolder;
  }
}
